/**
 * Leitura única do ambiente de build: **para onde** o painel fala e **qual evento** ele
 * serve.
 *
 * O painel original embutia a URL da API no código, o que impedia apontar o deploy
 * de `dev` para a API de homologação. Depois disso o default virou `localhost`, o
 * que é pior: um build de produção sem a variável subia apontando para a máquina de
 * quem abrisse o navegador, e falhava como se a API estivesse fora. Hoje o default da
 * URL existe **só em desenvolvimento**.
 *
 * O evento não tem default nenhum: é a chave da parametrização, e errá-lo lista as
 * inscrições de outro evento sem dar sinal. Ausente, a carga falha com mensagem.
 */

import { LISTING } from '@/shared/config/event.config';

/** Vale apenas com `vite dev`/`vitest`, onde a API local é o destino natural. */
const DEV_API_BASE_URL = 'http://localhost:3000';

function readApiBaseUrl(raw: string | undefined): string {
  const configured = raw?.trim();

  if (configured) {
    return configured.replace(/\/+$/, '');
  }

  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_API_BASE_URL não definida no build. Defina a base da ebs-approved-api, com o prefixo do gateway (ex.: https://api-externa.inteegra.com.br/<prefixo>), e rebuilde.',
    );
  }

  return DEV_API_BASE_URL;
}

/**
 * Evento do MICE que este deploy serve.
 *
 * Sem default, e obrigatória em qualquer modo: é a chave que busca a parametrização, e
 * um número errado (ou herdado de outro evento) faria o painel listar as inscrições
 * erradas — silenciosamente. Melhor não subir.
 *
 * Estava em `panel.settings.json`, o que obrigava a editar o repositório para apontar
 * outro evento. Como variável, o mesmo bundle serve qualquer evento parametrizado.
 */
function readMiceEventId(raw: string | undefined): number {
  const parsed = Number.parseInt(raw?.trim() ?? '', 10);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`VITE_MICE_EVENT_ID inválida ou não definida (recebido: ${JSON.stringify(raw)}). Defina o ID do evento no MICE (ex.: 183651).`);
  }

  return parsed;
}

/**
 * O tamanho de página vem da parametrização do evento; `VITE_PAGE_SIZE` só serve
 * para experimentar sem editar o documento, e é limitado ao mesmo teto que a API
 * aplica no servidor.
 */
function readPageSize(raw: string | undefined): number {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= LISTING.maxPageSize ? parsed : LISTING.pageSize;
}

export const env = {
  /** Base da API, sem barra final. */
  apiBaseUrl: readApiBaseUrl(import.meta.env.VITE_API_BASE_URL),
  /** Evento no MICE que este deploy serve. */
  miceEventId: readMiceEventId(import.meta.env.VITE_MICE_EVENT_ID),
  /** Registros por página na listagem. */
  pageSize: readPageSize(import.meta.env.VITE_PAGE_SIZE),
  isProduction: import.meta.env.PROD,
} as const;
